import React, {
  createRef, useCallback, useContext, useState,
} from 'react';
import {
  Box, Card, BoxProps, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import { LoadingButton } from '@mui/lab';
import { ButtonProps } from '@mui/material/Button/Button';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';

import { ITestingDashboardLoginForm, TestingDashboardLoginForm } from '../form/TestingDashboardLoginForm';
import { SessionService, TestingDashboardService } from '../../services';
import { ITestingDashboardLoginFormFields } from '../../schemas';
import { FeatureContext } from '../../FeatureContext';

const LoginWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '50px 0 50px 0',
  width: '100%',
}));

const LoginButton = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
  marginTop: '25px',
}));

export function TestingDashboardLogin() {
  const loginRef = createRef<ITestingDashboardLoginForm>();
  const navigate = useNavigate();
  const featureContext = useContext(FeatureContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState('');

  const onSubmit = useCallback(
    async (values: ITestingDashboardLoginFormFields) => {
      try {
        setIsLoading(true);
        setErrorCode('');
        const userRes = await TestingDashboardService.authorize(values.userId, values.password, featureContext.TESTING_DASHBOARD);
        SessionService.testingDashboardSignIn(userRes);
        navigate('/testingdashboard');
      } catch (err: any) {
        const errCode = err.response.data.message;
        setErrorCode(errCode);
        window.dataLayer.push({
          event: 'Exception',
          error_text: errCode,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [featureContext.TESTING_DASHBOARD, navigate],
  );

  return (
    <LoginWrapper>
      <Card sx={{ boxShadow: '0px 3px 6px #00000029' }} style={{ maxWidth: 400, width: '100%', padding: 16 }}>
        <TestingDashboardLoginForm onSubmit={onSubmit} ref={loginRef} />
        <LoginButton
          fullWidth
          variant="contained"
          loading={isLoading}
          endIcon={<LoginIcon />}
          onClick={() => loginRef.current?.handleSubmit()}
        >
          <FormattedMessage id="customer-login.button-signin" />
        </LoginButton>
        <Typography variant="body1" color="error" mt={1} mb={1}>
          {errorCode}
        </Typography>
      </Card>
    </LoginWrapper>
  );
}
